import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useParticipant } from '../hooks/useParticipant.js'
import { supabase } from '../lib/supabase.js'
import { fireBigCelebration } from '../components/ConfettiBurst.jsx'
import { play } from '../lib/sounds.js'
import { SKIP_CONNECT_KEY } from '../App.jsx'

/**
 * Step 3 of activation. Uses the Publora Workspace API to create a managed
 * user for the participant (first visit only), generate a fresh connection
 * URL, open it in a new tab, and poll the server until Publora reports a
 * LinkedIn connection is live. Then fires celebration and redirects to the
 * leaderboard.
 */
export default function ConnectLinkedIn() {
  const { participant, session, status } = useParticipant()
  const navigate = useNavigate()

  const [phase, setPhase] = useState('idle') // idle | initialising | ready | awaiting | connected | error
  const [connectionUrl, setConnectionUrl] = useState(null)
  const [error, setError] = useState(null)
  const popupRef = useRef(null)
  const pollRef = useRef(null)
  const pollStartedRef = useRef(0)
  // One-shot guards. The participant object updates on every Supabase realtime
  // event, which would otherwise re-fire the init effect and the success path
  // many times — spawning orphan Publora users, replaying confetti/sound,
  // and bouncing the UI between "Setting up…" and "You're in".
  const initialisedRef = useRef(false)
  const succeededRef = useRef(false)

  const token = session?.access_token

  const authedFetch = useCallback((path, body) => {
    return fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    })
  }, [token])

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Marks the page as connected exactly once — celebration, sound, redirect.
  // Idempotent: subsequent calls are no-ops, so realtime participant updates
  // can't replay the celebration or queue a second redirect.
  const markConnected = useCallback(() => {
    if (succeededRef.current) return
    succeededRef.current = true
    stopPolling()
    try { popupRef.current?.close?.() } catch (_) { /* popup may already be closed */ }
    setPhase('connected')
    fireBigCelebration()
    play('fanfare', { force: true })
    setTimeout(() => navigate('/', { replace: true }), 2200)
  }, [stopPolling, navigate])

  // One-shot check that doesn't run a polling loop. Used on mount + when the
  // tab regains visibility, so we catch a connection that happened off-screen
  // even if our polling timer has stopped.
  const checkOnce = useCallback(async () => {
    if (succeededRef.current) return true
    if (!token) return false
    try {
      const res = await authedFetch('/.netlify/functions/publora-check')
      const json = await res.json().catch(() => ({}))
      if (res.ok && json.connected) {
        markConnected()
        return true
      }
    } catch (err) {
      console.error('one-shot check failed:', err)
    }
    return false
  }, [token, authedFetch, markConnected])

  // Already-connected detector. If the participant row ever shows
  // publora_platform_id (either we set it in this session, or it was set by a
  // previous one), bounce to the leaderboard exactly once.
  useEffect(() => {
    if (!participant) return
    if (participant.publora_platform_id) markConnected()
  }, [participant?.publora_platform_id, markConnected])

  // Init flow: provision Publora user + grab a connection URL. Runs ONCE per
  // mount, regardless of how many times the participant object updates from
  // realtime. Without this guard, every realtime event re-resets phase to
  // "initialising" and re-creates a Publora user.
  useEffect(() => {
    if (!participant || !token) return
    if (participant.publora_platform_id) return // already connected, other effect handles it
    if (initialisedRef.current) return
    initialisedRef.current = true

    let cancelled = false
    setPhase('initialising')
    setError(null)

    ;(async () => {
      // First, check if Publora already has a LinkedIn link for us — covers the
      // case where the user OAuthed in a previous tab/session and we just never
      // recorded it.
      const alreadyConnected = await checkOnce()
      if (alreadyConnected || cancelled) return

      try {
        const res = await authedFetch('/.netlify/functions/publora-init')
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || `Init failed (${res.status})`)
        if (cancelled) return
        if (json.alreadyConnected) {
          markConnected()
          return
        }
        setConnectionUrl(json.url)
        setPhase('ready')
      } catch (err) {
        if (cancelled) return
        setError(err.message || 'Could not start connection')
        setPhase('error')
      }
    })()

    return () => { cancelled = true }
  // We deliberately key only on participant.id + token. Other participant
  // fields update during the flow and must not retrigger init.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant?.id, token])

  // Re-check the moment the tab becomes visible again. Publora's hosted page
  // can't redirect cross-origin back to us, so the most common pattern is the
  // user finishes auth in the popup, then comes back to our tab. Visibility
  // change is the cleanest signal that they're back.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible' && phase !== 'connected') {
        checkOnce()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [checkOnce, phase])

  // Polling loop. Runs while we're waiting for Publora to confirm the link.
  const startPolling = useCallback(() => {
    stopPolling()
    pollStartedRef.current = Date.now()

    const poll = async () => {
      if (succeededRef.current) { stopPolling(); return }
      try {
        const res = await authedFetch('/.netlify/functions/publora-check')
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'Check failed')

        if (json.connected) {
          markConnected()
          return
        }

        // User closed the popup before authorising — drop them back to ready
        // so they can hit Retry without waiting 5 minutes.
        if (popupRef.current && popupRef.current.closed) {
          stopPolling()
          setPhase('ready')
          return
        }

        // Time out after 5 min of polling so we don't bang the API forever.
        if (Date.now() - pollStartedRef.current > 5 * 60 * 1000) {
          stopPolling()
          setPhase('ready')
        }
      } catch (err) {
        console.error('poll error:', err)
      }
    }

    pollRef.current = setInterval(poll, 2500)
    poll() // fire once immediately
  }, [authedFetch, stopPolling, navigate])

  useEffect(() => stopPolling, [stopPolling])

  const handleConnect = useCallback(() => {
    if (!connectionUrl) return
    // Deliberately NOT using noopener/noreferrer — we need the popup reference
    // so we can detect when the user closes it AND so we can close it ourselves
    // once Publora confirms the connection. Some browsers (Safari, Brave w/
    // shields) still silently block; in that case we fall through to a same-tab
    // navigation so the user always gets to the OAuth page.
    const popup = window.open(connectionUrl, 'publora-connect', 'width=560,height=720')
    if (!popup || popup.closed) {
      window.location.href = connectionUrl
      return
    }
    popupRef.current = popup
    setPhase('awaiting')
    startPolling()
  }, [connectionUrl, startPolling])

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-brand-blue animate-pulse" />
          <span className="text-brand-text-muted text-sm">Loading…</span>
        </div>
      </div>
    )
  }
  if (!participant) return <Navigate to="/welcome" replace />

  return (
    <div className="min-h-screen mesh-bg flex flex-col">
      <header className="px-6 py-4">
        <Link to="/welcome" className="inline-block">
          <img src="/climatise-logo.svg" alt="Climatise" className="h-8" />
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-card p-8 border border-brand-border"
        >
          <AnimatePresence mode="wait">
            {(phase === 'idle' || phase === 'initialising') && (
              <StateCard key="loading" icon="⏳" title="Setting up…" body="Creating your LinkedIn connection." />
            )}

            {phase === 'ready' && (
              <StateCard
                key="ready"
                icon="🔗"
                title="Connect LinkedIn"
                body="Publora reads impressions and reactions on your posts. Read-only. Disconnect any time."
                bullets={[
                  '10 seconds',
                  'Nothing posts on your behalf',
                  'Revoke from LinkedIn settings any time',
                ]}
                primary={{ label: 'Connect LinkedIn', onClick: handleConnect }}
              />
            )}

            {phase === 'awaiting' && (
              <StateCard
                key="awaiting"
                icon="👀"
                title="Waiting for LinkedIn…"
                body={<>Finish authorising in the Publora tab. Come back here when you're done — we'll detect it automatically.</>}
                bullets={[
                  'Still on the Publora tab? Click "Connect LinkedIn" there and authorise.',
                  'Already finished? Tap "I\'ve connected" below.',
                ]}
                primary={{ label: 'I\'ve connected — check now', onClick: () => checkOnce() }}
                secondary={{ label: 'Open Publora again', onClick: handleConnect }}
                subtle
              />
            )}

            {phase === 'connected' && (
              <motion.div key="connected" className="text-center" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 220, damping: 18 }}>
                <div className="text-7xl mb-4">🎉</div>
                <h1 className="text-3xl font-bold text-brand-text mb-2">You're in.</h1>
                <p className="text-brand-text-muted">Loading the leaderboard…</p>
              </motion.div>
            )}

            {phase === 'error' && (
              <StateCard
                key="error"
                icon="⚠️"
                title="Connection didn't start"
                body={error || 'Publora returned an error. Ping Lennon.'}
                primary={{ label: 'Try again', onClick: () => { setPhase('idle'); setConnectionUrl(null); setError(null); window.location.reload() } }}
              />
            )}
          </AnimatePresence>

          <button
            onClick={() => {
              sessionStorage.setItem(SKIP_CONNECT_KEY, '1')
              navigate('/')
            }}
            className="block w-full text-xs text-brand-text-muted mt-6 hover:text-brand-text"
          >
            Skip for now. Stats start once you connect.
          </button>
        </motion.div>
      </div>
    </div>
  )
}

function StateCard({ icon, title, body, bullets, primary, secondary, subtle }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="text-5xl mb-3 text-center">{icon}</div>
      <h2 className="text-2xl font-bold text-brand-text mb-2 text-center">{title}</h2>
      <p className="text-sm text-brand-text-muted mb-6 text-center">{body}</p>

      {bullets && (
        <div className="bg-brand-bg-muted rounded-xl p-4 text-xs text-brand-text mb-6 space-y-2">
          {bullets.map((b, i) => (
            <div key={i} className="flex gap-2"><span>✓</span><span>{b}</span></div>
          ))}
        </div>
      )}

      {primary && (
        <button
          onClick={primary.onClick}
          className={`w-full font-semibold py-3 rounded-lg transition-colors ${
            subtle
              ? 'bg-brand-blue hover:bg-brand-blue-hover text-white'
              : 'bg-brand-blue hover:bg-brand-blue-hover text-white'
          }`}
        >
          {primary.label}
        </button>
      )}

      {secondary && (
        <button
          onClick={secondary.onClick}
          className="w-full mt-2 font-medium text-sm py-2.5 rounded-lg text-brand-text-muted hover:text-brand-text hover:bg-brand-bg-muted transition-colors"
        >
          {secondary.label}
        </button>
      )}
    </motion.div>
  )
}

